import {
  PlusOutlined,
  EditOutlined,
  AppstoreOutlined,
  CalculatorOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  Modal,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import ExportableFilterableTable from "../../components/tables/ExportableFilterableTable";
import { COLORS } from "../../components/ui/colors";
import axiosInstance from "../../variables/api/axiosInstance";

const { Title, Text } = Typography;
const { TextArea } = Input;

const ModulesV2 = () => {
  const [modules, setModules] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [nom, setNom] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [comptable, setComptable] = useState(false);

  const fetchModules = () => {
    setLoading(true);
    axiosInstance
      .get(`/api/module`)
      .then((res) => {
        setModules(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchModules();
  }, []);

  const resetForm = () => {
    setNom("");
    setCommentaire("");
    setComptable(false);
    setEditMode(false);
    setSelectedModule(null);
  };

  const handleCreate = () => {
    resetForm();
    setOpen(true);
  };

  const handleEdit = (row) => {
    setEditMode(true);
    setSelectedModule(row);
    setNom(row.nom);
    setCommentaire(row.commentaire);
    setComptable(row.comptable);
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = () => {
    if (!nom || !commentaire) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }
    setSubmitting(true);

    if (editMode) {
      const payload = { ...selectedModule, nom, commentaire, comptable };
      axiosInstance
        .put(`/api/module`, payload)
        .then((res) => {
          setModules((prev) => prev.map((m) => (m.moduleId === payload.moduleId ? res.data : m)));
          toast.success("Module modifié avec succès");
          handleCancel();
        })
        .catch((err) => {
          console.log(err);
          toast.error("Erreur lors de la modification du module");
        })
        .finally(() => setSubmitting(false));
    } else {
      axiosInstance
        .post(`/api/module`, { nom, commentaire, comptable })
        .then((res) => {
          setModules((prev) => [...prev, res.data]);
          toast.success("Module ajouté avec succès");
          handleCancel();
        })
        .catch((err) => {
          console.log(err);
          toast.error("Erreur lors de l'ajout du module");
        })
        .finally(() => setSubmitting(false));
    }
  };

  const columns = [
    {
      title: "Module",
      dataIndex: "nom",
      key: "nom",
      searchable: true,
      render: (_, record) => (
        <Space>
          <Avatar
            shape="square"
            style={{ backgroundColor: record.comptable ? COLORS.success : COLORS.primary, flexShrink: 0 }}
            icon={record.comptable ? <CalculatorOutlined /> : <AppstoreOutlined />}
          />
          <span style={{ fontWeight: 600, color: COLORS.secondary }}>{record.nom}</span>
        </Space>
      ),
    },
    {
      title: "Description",
      dataIndex: "commentaire",
      key: "commentaire",
      searchable: true,
      render: (text) => text || <Text type="secondary">—</Text>,
    },
    {
      title: "Comptable",
      dataIndex: "comptable",
      key: "comptable",
      width: 150,
      align: "center",
      filterType: "select",
      filterOptions: [
        { label: "Comptable", value: "Oui" },
        { label: "Standard", value: "Non" },
      ],
      filterMatcher: (cellValue, filterValue) => (cellValue ? "Oui" : "Non") === filterValue,
      exportValue: (row) => (row.comptable ? "Oui" : "Non"),
      render: (comptable) =>
        comptable ? (
          <Tag icon={<CalculatorOutlined />} color="green" style={{ borderRadius: 6 }}>
            Comptable
          </Tag>
        ) : (
          <Tag color="default" style={{ borderRadius: 6 }}>
            Standard
          </Tag>
        ),
    },
    {
      title: "Actions",
      key: "action",
      hideInExport: true,
      width: 90,
      align: "center",
      render: (_, record) => (
        <Tooltip title="Modifier">
          <Button
            type="text"
            shape="circle"
            icon={<EditOutlined style={{ color: COLORS.primary }} />}
            onClick={() => handleEdit(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"DROITS"} />

        <div className="layout-page">
          <NavBar />

          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              {/* En-tete */}
              <Card
                bordered={false}
                style={{
                  borderRadius: 16,
                  marginBottom: 20,
                  background: COLORS.gradient,
                  boxShadow: "0 6px 20px rgba(36,33,102,0.25)",
                }}
                bodyStyle={{ padding: "22px 28px" }}
              >
                <Row align="middle" justify="space-between" gutter={[16, 16]}>
                  <Col>
                    <Space align="center" size="middle">
                      <div
                        style={{
                          width: 56, height: 56, borderRadius: 14,
                          background: "rgba(255,255,255,0.18)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        <AppstoreOutlined style={{ fontSize: 28, color: "#fff" }} />
                      </div>
                      <div>
                        <Title level={3} style={{ color: "#fff", margin: 0 }}>
                          Modules de l'application
                        </Title>
                        <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                          Domaines fonctionnels regroupant les habilitations
                        </Text>
                      </div>
                    </Space>
                  </Col>
                  <Col>
                    <Button
                      size="large"
                      icon={<PlusOutlined />}
                      onClick={handleCreate}
                      style={{
                        background: "#fff",
                        color: COLORS.secondary,
                        fontWeight: 600,
                        border: "none",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                      }}
                    >
                      Ajouter un module
                    </Button>
                  </Col>
                </Row>
              </Card>

              {/* Tableau */}
              <Card
                bordered={false}
                title={
                  <Space>
                    <AppstoreOutlined style={{ color: COLORS.primary }} />
                    <span>Liste des modules</span>
                  </Space>
                }
                extra={<Tag color="blue">{modules.length} module{modules.length > 1 ? "s" : ""}</Tag>}
                style={{ borderRadius: 16, boxShadow: "0 4px 16px rgba(0,0,0,0.06)" }}
                bodyStyle={{ paddingTop: 12 }}
              >
                <ExportableFilterableTable
                  columns={columns}
                  dataSource={modules}
                  exportDataSource={modules}
                  rowKey="moduleId"
                  loading={loading}
                  searchPlaceholder="Rechercher un module..."
                  exportFileName="modules"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total : ${total} module(s)`,
                  }}
                />
              </Card>

              <Footer />
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={open}
        onOk={handleSubmit}
        onCancel={handleCancel}
        confirmLoading={submitting}
        title={editMode ? "Modifier un module" : "Ajouter un module"}
        okText={editMode ? "Modifier" : "Ajouter"}
        cancelText="Annuler"
        centered
      >
        <Space direction="vertical" size="middle" style={{ width: "100%", marginTop: 8 }}>
          <div>
            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
              Nom du module
            </label>
            <Input
              placeholder="Nom du module"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
            />
          </div>
          <div>
            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
              Description
            </label>
            <TextArea
              rows={3}
              placeholder="Description du module"
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
            />
          </div>
          <Checkbox checked={comptable} onChange={(e) => setComptable(e.target.checked)}>
            Module comptable ?
          </Checkbox>
        </Space>
      </Modal>

      <div className="layout-overlay layout-menu-toggle"></div>
      <div className="drag-target"></div>
    </div>
  );
};

export default ModulesV2;
